import React, { useState } from 'react';
import { User, Mail, Phone, MapPin, Calendar, Globe, Building2, AlertCircle, FileText, Euro, Users, ChevronDown, ChevronUp, Shield, Briefcase } from 'lucide-react';
import { PersonDto } from 'api/actor';
import { LegalEntityDto, NaturalPersonDto } from 'api/borrower';
import { ReferentialKey } from 'api/referential';
import { useAppSelector } from 'app/hooks';
import { selectReferentialData } from 'features/referentials/selectors/selectReferentialDataByKey';

// Natural Person Card Component
const NaturalPersonCard: React.FC<{ person: NaturalPersonDto }> = ({ person }) => {

  const referentialsData = useAppSelector(selectReferentialData)

  const getReferentialLabel = (referentialKey: ReferentialKey, id?: string | null) => {
    const referential = referentialsData[referentialKey].find((item: any) => item.id === id);
    return referential ? (referential.label as string) : undefined;
  };

  const getReferentialLabelByCode = (referentialKey: ReferentialKey, code?: string | null) => {
    const referential = referentialsData[referentialKey].find((item: any) => item.code === code);
    return referential ? (referential.label as string) : undefined;
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
      <div className="bg-primary p-8 border-b border-primary/10">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white">
                {person.firstName} {person.lastName}
              </h2>
              {person.birthName && (
                <p className="text-sm text-white mt-1">
                  Nom de naissance: {person.birthName}
                </p>
              )}
              {person.companyRole && (
                <div className="flex items-center gap-2 mt-2">
                  <Building2 className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">{person.companyRole}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-6">
        <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Informations de contact
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Colonne Email et Téléphone */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 bg-white rounded-lg p-4 border border-primary/10">
                <Mail className="w-5 h-5 text-primary/70" />
                <div>
                  <p className="text-xs text-gray-500 font-medium">Email</p>
                  <p className="text-sm text-gray-900">{person.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white rounded-lg p-4 border border-primary/10">
                <Phone className="w-5 h-5 text-primary/70" />
                <div>
                  <p className="text-xs text-gray-500 font-medium">Téléphone</p>
                  <p className="text-sm text-gray-900">{person.phone}</p>
                </div>
              </div>
            </div>

            {/* Colonne Adresse */}
            <div className="flex items-start gap-3 bg-white rounded-lg p-4 border border-primary/10">
              <MapPin className="w-5 h-5 text-primary/70 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-gray-500 font-medium mb-2">Adresse</p>
                <div className="space-y-1">
                  {person.address?.line1 && (
                    <p className="text-sm text-gray-900">{person.address.line1}</p>
                  )}
                  {person.address?.zipCode && person.address?.city && (
                    <p className="text-sm text-gray-900">
                      {person.address.zipCode} {person.address.city}
                    </p>
                  )}
                  {person.address?.country && (
                    <p className="text-sm text-gray-700 flex items-center gap-1.5 mt-2">
                      <Globe className="w-3.5 h-3.5 text-primary/60" />
                      {getReferentialLabelByCode(ReferentialKey.Country, person.address.country)}
                    </p>
                  )}
                  {!person.address?.line1 && !person.address?.city && !person.address?.country && (
                    <p className="text-sm text-gray-400 italic">Non renseignée</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Informations personnelles
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-primary/10">
                <span className="text-sm text-gray-600">Date de naissance</span>
                <span className="text-sm font-medium text-gray-900">{formatDate(person.birthDate)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-primary/10">
                <span className="text-sm text-gray-600">Civilité</span>
                <span className="text-sm font-medium text-gray-900">{getReferentialLabel(ReferentialKey.Civility, person.civilityId)}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600">Pays de naissance</span>
                <span className="text-sm font-medium text-gray-900">{getReferentialLabel(ReferentialKey.Country, person.birthCountryId)}</span>
              </div>
            </div>
          </div>

          <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              Informations fiscales
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-primary/10">
                <span className="text-sm text-gray-600">Nationalité</span>
                <span className="text-sm font-medium text-gray-900">{getReferentialLabel(ReferentialKey.Nationality, person.nationalityId)}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600">Résidence fiscale</span>
                <span className="text-sm font-medium text-gray-900">{getReferentialLabel(ReferentialKey.Country, person.taxResidenceCountryId)}</span>
              </div>
            </div>
          </div>
        </div>

        {(person.isPPE || person.isFPE) && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">Attention particulière requise</p>
              <p className="text-xs text-amber-700 mt-1">
                Cette personne est identifiée comme {person.isPPE && 'Personne Politiquement Exposée (PPE)'}
                {person.isPPE && person.isFPE && ' et '}
                {person.isFPE && 'Famille/Proche de Personne Exposée (FPE)'}.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Legal Entity Card Component
const LegalEntityCard: React.FC<{ entity: LegalEntityDto }> = ({ entity }) => {
  const [showPersons, setShowPersons] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
      <div className="bg-primary p-8 border-b border-primary/10">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white">{entity.companyName}</h2>
              <p className="text-sm text-gray-200 mt-1">{entity.legalFormId}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full border border-primary/20">
                  SIRET: {entity.registrationNumber}
                </span>
              </div>
            </div>
          </div>

          <div className="text-right">
            <p className="text-sm text-gray-500">Capital social</p>
            <p className="text-2xl font-bold text-primary">{formatCurrency(entity.capital)}</p>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-6">
        <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Coordonnées
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 bg-white rounded-lg p-4 border border-primary/10">
              <Mail className="w-5 h-5 text-primary/70" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500 font-medium">Email</p>
                <p className="text-sm text-gray-900 truncate">{entity.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-4 border border-primary/10">
              <Phone className="w-5 h-5 text-primary/70" />
              <div>
                <p className="text-xs text-gray-500 font-medium">Téléphone</p>
                <p className="text-sm text-gray-900">{entity.phone}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Adresse du siège
            </h3>
            <div className="bg-white rounded-lg p-4 border border-primary/10">
              <p className="text-sm text-gray-900">
                {entity.address?.line1 && <span className="block">{entity.address.line1}</span>}
                {entity.address?.zipCode && entity.address?.city && (
                  <span className="block mt-1">{entity.address.zipCode} {entity.address.city}</span>
                )}
                {entity.address?.country && (
                  <span className="block mt-1 text-gray-600">{entity.address.country}</span>
                )}
                {!entity.address?.line1 && !entity.address?.city && !entity.address?.country && (
                  <span className="block text-gray-400 italic">Non renseignée</span>
                )}
              </p>
            </div>
          </div>

          <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Immatriculation
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 bg-white rounded-lg px-4 border border-primary/10">
                <span className="text-sm text-gray-600">RCS</span>
                <span className="text-sm font-medium text-gray-900">{entity.rcsCity}</span>
              </div>
              <div className="flex justify-between items-center py-2 bg-white rounded-lg px-4 border border-primary/10">
                <span className="text-sm text-gray-600">N° RCS</span>
                <span className="text-sm font-medium text-gray-900">{entity.rcsNumber}</span>
              </div>
            </div>
          </div>
        </div>

        {(entity.oriasNumber || entity.rcp) && (
          <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Informations professionnelles
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {entity.oriasNumber && (
                <div className="flex items-center gap-3 bg-white rounded-lg p-4 border border-primary/10">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Numéro ORIAS</p>
                    <p className="text-sm font-semibold text-gray-900">{entity.oriasNumber}</p>
                  </div>
                </div>
              )}
              {entity.rcp && (
                <div className="flex items-center gap-3 bg-white rounded-lg p-4 border border-primary/10">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">RCP</p>
                    <p className="text-sm font-semibold text-gray-900">{entity.rcp}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {entity.naturalPersons.length > 0 && (
          <div className="bg-primary/5 rounded-xl border border-primary/10 overflow-hidden">
            <button
              onClick={() => setShowPersons(!showPersons)}
              className="w-full p-6 flex items-center justify-between hover:bg-primary/10 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Personnes associées ({entity.naturalPersons.length})
                </h3>
              </div>
              {showPersons ? (
                <ChevronUp className="w-5 h-5 text-primary" />
              ) : (
                <ChevronDown className="w-5 h-5 text-primary" />
              )}
            </button>

            {showPersons && (
              <div className="p-6 pt-0 space-y-3">
                {entity.naturalPersons.map((person, index) => (
                  <div
                    key={person.id || index}
                    className="bg-white rounded-lg p-4 border border-primary/10 hover:border-primary/30 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-primary">
                            {person.firstName.charAt(0)}{person.lastName.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {person.firstName} {person.lastName}
                          </p>
                          {person.companyRole && (
                            <p className="text-sm text-primary font-medium">{person.companyRole}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {person.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {person.phone}
                            </span>
                          </div>
                        </div>
                      </div>

                      {(person.isPPE || person.isFPE) && (
                        <div className="flex gap-1">
                          {person.isPPE && (
                            <span className="px-2 py-0.5 bg-amber-500/10 text-amber-700 text-xs font-semibold rounded border border-amber-500/20">
                              PPE
                            </span>
                          )}
                          {person.isFPE && (
                            <span className="px-2 py-0.5 bg-red-500/10 text-red-700 text-xs font-semibold rounded border border-red-500/20">
                              FPE
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const PersonDetails: React.FC<{ person: PersonDto }> = ({ person }) => {
  if (person.type === 'physique') {
    return <NaturalPersonCard person={person} />;
  }

  if (person.type === 'morale') {
    return <LegalEntityCard entity={person} />;
  }

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <p className="text-red-800">Type de personne non reconnu: {(person as any).type}</p>
    </div>
  );
};
